import { DetailHeader } from "@elyxeer/ui";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Snackbar from "@mui/material/Snackbar";
import Typography from "@mui/material/Typography";
import type { FormEvent, ReactNode } from "react";
import { useTranslation } from "react-i18next";

import type { SaveStatus } from "~/components/settings/use-profile-save";
import { Screen } from "~/components/ui/Screen";
import { SNACKBAR_POSITION } from "~/components/ui/snackbar";

/**
 * The frame every settings sub-screen shares: a way back, a heading, one line of
 * explanation, the controls, the primary action and the save feedback.
 *
 * Keeping it here means a new setting is a form and two i18n keys — it cannot arrive
 * with a different heading size, a missing back link or a swallowed error.
 *
 * The frame owns the `<form>` rather than the screen inside it, so `action` can hold
 * the real submit button while sitting at the far end of the page from the field.
 */
export function SettingsPage({
  title,
  description,
  children,
  action,
  onSubmit,
  status = "idle",
  onDismiss,
  backTo = "/profile",
  backLabel,
}: {
  title: string;
  description?: string;
  children: ReactNode;
  /**
   * Where "back" goes. The profile menu unless a setting is also reachable from
   * elsewhere and knows it was opened from there.
   */
  backTo?: string;
  /** Resolved label for `backTo`; defaults to "back to profile". */
  backLabel?: string;
  /** Primary action, docked to the bottom of the screen and full width. */
  action?: ReactNode;
  /** Present when the screen commits on submit; makes the frame a `<form>`. */
  onSubmit?: (event: FormEvent) => void;
  /** Outcome of the last save, from `useProfileSave`. */
  status?: SaveStatus;
  onDismiss?: () => void;
}) {
  const { t } = useTranslation();

  return (
    <Screen
      maxWidth="lg"
      sx={{ minHeight: "100dvh", display: "flex", flexDirection: "column" }}
    >
      <Box
        component={onSubmit ? "form" : "div"}
        onSubmit={onSubmit}
        sx={{
          // Grow to the full screen so `action` can be pushed to the bottom of it;
          // `Screen` has already reserved what the tab bar and home indicator need.
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
          gap: { xs: 3, md: 4 },
        }}
      >
        <DetailHeader
          backTo={backTo}
          backLabel={backLabel ?? t("common.backToProfile")}
          backTestId="settings.back"
        />

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1,
            maxWidth: 560,
          }}
        >
          <Typography variant="H1" sx={{ color: "text.primary" }}>
            {title}
          </Typography>
          {description ? (
            <Typography variant="T2" sx={{ color: "text.secondary" }}>
              {description}
            </Typography>
          ) : null}
        </Box>

        {/*
          No card around the controls. The profile menu is a card because it is a list of
          rows that needs an edge; a sub-screen is one field under its own heading, and
          boxing a single filled input inside an outlined panel reads as two nested
          surfaces for one control.
        */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2.5,
            maxWidth: 560,
          }}
        >
          {children}
        </Box>

        {/*
          Docked, not trailing the field: on a phone the primary action of a screen
          belongs under the thumb, and a button left floating beneath a single input
          reads as part of the input rather than as the end of the screen. Only on a
          phone, though — pushing it to the foot of a desktop window opens a void
          between the field and its own Save button.
        */}
        {action ? (
          <Box sx={{ marginTop: { xs: "auto", md: 0 }, maxWidth: 560 }}>
            {action}
          </Box>
        ) : null}
      </Box>

      <Snackbar
        open={status === "saved"}
        autoHideDuration={3000}
        onClose={onDismiss}
        message={t("profile.saved")}
        sx={SNACKBAR_POSITION}
        data-testid="settings.saved"
      />

      <Snackbar
        open={status === "failed"}
        autoHideDuration={5000}
        onClose={onDismiss}
        sx={SNACKBAR_POSITION}
      >
        <Alert severity="error" onClose={onDismiss}>
          {t("common.somethingWentWrong")}
        </Alert>
      </Snackbar>
    </Screen>
  );
}
